import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Search as SearchIcon, PlusCircle, X, Check, ChevronsUpDown, Upload, Camera, Download } from "lucide-react";
import { toast } from "sonner";
import { ExcelImportDialog } from "@/components/ExcelImportDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Book, BookCopy, BookFormData, MasterItem, ColumnFilterOption } from "@/types";

const emptyBookForm = (): BookFormData => ({
  title: "",
  isbn: "",
  authors: "",
  category_id: "",
  publisher_id: "",
  publication_year: "",
  edition: "",
  pages: "",
  language: "English",
  cover_image_url: "",
  description: "",
  copies: "1",
});

export default function AccessionRegisterPage() {
  const { api, user } = useAuth();
  const [books, setBooks] = useState<Book[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Book | null>(null);
  const [categories, setCategories] = useState<MasterItem[]>([]);
  const [publishers, setPublishers] = useState<MasterItem[]>([]);
  const [authorsList, setAuthorsList] = useState<MasterItem[]>([]);
  const [isbnQuery, setIsbnQuery] = useState("");
  const [isbnLoading, setIsbnLoading] = useState(false);
  const [aiEnrichLoading, setAiEnrichLoading] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerLoading, setScannerLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedAuthorIds, setSelectedAuthorIds] = useState<string[]>([]);
  const [quickAddType, setQuickAddType] = useState<"" | "author" | "category" | "publisher">("");
  const [quickAddName, setQuickAddName] = useState("");
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const filterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);

  const [form, setForm] = useState<BookFormData>(emptyBookForm());
  const [existingCopyAccessions, setExistingCopyAccessions] = useState<string[]>([]);
  const [previewAccessions, setPreviewAccessions] = useState<string[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [accessionInput, setAccessionInput] = useState("");
  const [takenAccessions, setTakenAccessions] = useState<string[]>([]);
  const [checkingAccessions, setCheckingAccessions] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "librarian";

  const fetchBooks = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20 };
      const textParts = [columnFilters.title, columnFilters.isbn, columnFilters.authors].filter(Boolean);
      if (textParts.length) params.search = textParts.join(" ");
      if (columnFilters.category_id) params.category_id = columnFilters.category_id;
      if (columnFilters.publisher_id) params.publisher_id = columnFilters.publisher_id;
      const res = await api.get("/books", { params });
      setBooks(res.data.items);
      setTotalPages(res.data.pages);
    } catch {
      toast.error("Failed to load books");
    } finally {
      setLoading(false);
    }
  }, [api, page, columnFilters]);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const stopCamera = useCallback(() => {
    const stream = cameraStreamRef.current;
    if (!stream) return;
    stream.getTracks().forEach((track) => track.stop());
    cameraStreamRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const openCameraScanner = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Camera is not supported in this browser");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      cameraStreamRef.current = stream;
      setScannerOpen(true);

      // Let the dialog render before attaching stream to video element.
      setTimeout(() => {
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
          void cameraVideoRef.current.play().catch(() => {
            toast.error("Could not start camera preview");
          });
        }
      }, 0);
    } catch {
      toast.error("Could not access camera");
      stopCamera();
    }
  };

  const extractISBN = (text: string): string | null => {
    if (!text) return null;
    const compact = text.replace(/[^0-9Xx]/g, "").toUpperCase();
    const isbn13 = compact.match(/(?:97[89]\d{10})/);
    if (isbn13) return isbn13[0];

    const isbn10 = compact.match(/(?:\d{9}[\dX])/);
    if (isbn10) return isbn10[0];

    return null;
  };

  const captureAndScanISBN = async () => {
    const video = cameraVideoRef.current;
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
      toast.error("Camera preview is not ready yet");
      return;
    }

    const BarcodeDetectorCtor = (window as any).BarcodeDetector;
    if (!BarcodeDetectorCtor) {
      toast.error("Barcode scan is not supported in this browser");
      return;
    }

    setScannerLoading(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        toast.error("Could not process captured image");
        return;
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const detector = new BarcodeDetectorCtor({
        formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128"],
      });

      const imageBitmap = await createImageBitmap(canvas);
      const detections = await detector.detect(imageBitmap);
      imageBitmap.close();

      const match = detections
        .map((d: any) => String(d.rawValue || ""))
        .map((value: string) => extractISBN(value))
        .find(Boolean);

      if (!match) {
        toast.error("Could not detect an ISBN. Try a clearer image.");
        return;
      }

      setForm((prev) => ({ ...prev, isbn: match }));
      setIsbnQuery(match);
      toast.success(`ISBN detected: ${match}`);
      setScannerOpen(false);
      stopCamera();
    } catch {
      toast.error("Failed to scan ISBN from captured image");
    } finally {
      setScannerLoading(false);
    }
  };

  const loadMasters = useCallback(async () => {
    try {
      const [c, p, a] = await Promise.all([api.get("/masters/categories/all"), api.get("/masters/publishers/all"), api.get("/masters/authors/all")]);
      setCategories(c.data);
      setPublishers(p.data);
      setAuthorsList(a.data);
    } catch {}
  }, [api]);

  useEffect(() => {
    loadMasters();
  }, [loadMasters]);

  const fetchNextAccessions = useCallback(
    async (count: number) => {
      if (count < 1) {
        setPreviewAccessions([]);
        return;
      }
      setPreviewLoading(true);
      try {
        const res = await api.get("/books/copies/next-accession", { params: { count } });
        setPreviewAccessions(res.data.accession_numbers || []);
      } catch {
        setPreviewAccessions([]);
        toast.error("Failed to calculate accession numbers");
      } finally {
        setPreviewLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    if (!dialogOpen) return;
    const copyCount = parseInt(form.copies, 10) || 0;
    const needed = editing ? copyCount : Math.max(copyCount, 1);
    if (needed < 1) {
      setPreviewAccessions([]);
      return;
    }
    void fetchNextAccessions(needed);
  }, [dialogOpen, editing, form.copies, fetchNextAccessions]);

  const parsedAccessions = accessionInput
    .split(/[,\n]/)
    .map((value) => value.trim())
    .filter(Boolean);
  const expectedAccessionCount = editing ? parseInt(form.copies, 10) || 0 : Math.max(parseInt(form.copies, 10) || 0, 1);
  const duplicateAccessions = Array.from(new Set(parsedAccessions.filter((value, index) => parsedAccessions.indexOf(value) !== index)));
  const conflictingAccessions = parsedAccessions.filter((value) => takenAccessions.includes(value));

  let accessionError = "";
  if (expectedAccessionCount > 0) {
    if (parsedAccessions.length !== expectedAccessionCount) {
      accessionError = `Enter ${expectedAccessionCount} accession number(s) — ${parsedAccessions.length} entered.`;
    } else if (duplicateAccessions.length) {
      accessionError = `Duplicate in list: ${duplicateAccessions.join(", ")}`;
    } else if (conflictingAccessions.length) {
      accessionError = `Already in register: ${conflictingAccessions.join(", ")}`;
    }
  }

  const checkAccessionsTaken = useCallback(
    async (numbers: string[]) => {
      if (numbers.length === 0) return [];
      const res = await api.get("/books/copies/check-accession", { params: { numbers: numbers.join(",") } });
      return (res.data.taken || []).map(String) as string[];
    },
    [api],
  );

  const parsedAccessionsKey = parsedAccessions.join(",");
  useEffect(() => {
    if (!dialogOpen || !parsedAccessionsKey) {
      setTakenAccessions([]);
      setCheckingAccessions(false);
      return;
    }
    let cancelled = false;
    setCheckingAccessions(true);
    const timer = setTimeout(async () => {
      try {
        const taken = await checkAccessionsTaken(parsedAccessionsKey.split(","));
        if (!cancelled) setTakenAccessions(taken);
      } catch {
        if (!cancelled) setTakenAccessions([]);
      } finally {
        if (!cancelled) setCheckingAccessions(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dialogOpen, parsedAccessionsKey, checkAccessionsTaken]);

  const handleColumnFiltersChange = useCallback((filters: Record<string, any>) => {
    setColumnFilters(filters);
    if (filterTimer.current) clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => setPage(1), 400);
  }, []);

  const enrichWithAi = async () => {
    if (!form.title && !form.isbn && !form.description) {
      toast.error("Enter a title, ISBN, or description first");
      return;
    }
    setAiEnrichLoading(true);
    try {
      const res = await api.post("/books/ai/enrich", {
        title: form.title || undefined,
        isbn: form.isbn || isbnQuery || undefined,
        authors: form.authors || undefined,
        description: form.description || undefined,
      });
      const data = res.data;
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        authors: Array.isArray(data.authors) && data.authors.length ? data.authors.join(", ") : f.authors,
        description: data.description || f.description,
      }));
      toast.success("AI suggestions applied");
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "AI enrichment unavailable";
      toast.error(detail);
    } finally {
      setAiEnrichLoading(false);
    }
  };

  const lookupISBN = async () => {
    if (!isbnQuery) return;
    setIsbnLoading(true);
    try {
      const res = await api.get(`/books/isbn/${isbnQuery}`);
      const data = res.data;

      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        isbn: isbnQuery,
        authors: (data.authors || []).join(", "),
        publication_year: String(data.publication_year || ""),
        pages: String(data.pages || ""),
        cover_image_url: data.cover_image_url || f.cover_image_url,
        description: data.description || f.description,
      }));

      // Auto-resolve and create missing master data from ISBN
      let mastersChanged = false;
      const resolvedAuthorIds: string[] = [];

      // Resolve authors
      for (const name of (data.authors || []) as string[]) {
        if (!name.trim()) continue;
        const match = authorsList.find((a) => a.name.toLowerCase() === name.trim().toLowerCase());
        if (match) {
          resolvedAuthorIds.push(match.id);
        } else {
          try {
            const created = await api.post("/masters/authors", { name: name.trim() });
            resolvedAuthorIds.push(String(created.data.id));
            mastersChanged = true;
          } catch {}
        }
      }

      // Resolve publisher
      let resolvedPublisherId = "";
      const pubNames: string[] = data.publishers || [];
      if (pubNames.length > 0 && pubNames[0].trim()) {
        const match = publishers.find((p) => p.name.toLowerCase() === pubNames[0].trim().toLowerCase());
        if (match) {
          resolvedPublisherId = match.id;
        } else {
          try {
            const created = await api.post("/masters/publishers", { name: pubNames[0].trim() });
            resolvedPublisherId = String(created.data.id);
            mastersChanged = true;
          } catch {}
        }
      }

      // Resolve category
      let resolvedCategoryId = "";
      const catNames: string[] = data.categories || [];
      if (catNames.length > 0 && catNames[0].trim()) {
        const match = categories.find((c) => c.name.toLowerCase() === catNames[0].trim().toLowerCase());
        if (match) {
          resolvedCategoryId = match.id;
        } else {
          try {
            const created = await api.post("/masters/categories", { name: catNames[0].trim() });
            resolvedCategoryId = String(created.data.id);
            mastersChanged = true;
          } catch {}
        }
      }

      // Reload master lists if new entries were created
      if (mastersChanged) await loadMasters();

      // Set resolved IDs into the form
      if (resolvedAuthorIds.length > 0) setSelectedAuthorIds(resolvedAuthorIds);
      if (resolvedPublisherId) setForm((f) => ({ ...f, publisher_id: resolvedPublisherId }));
      if (resolvedCategoryId) setForm((f) => ({ ...f, category_id: resolvedCategoryId }));

      const autoCreated: string[] = [];
      if (mastersChanged) {
        if (
          resolvedAuthorIds.length >
          (data.authors || []).filter((n: string) => authorsList.find((a) => a.name.toLowerCase() === n.trim().toLowerCase())).length
        )
          autoCreated.push("authors");
        if (resolvedPublisherId && !publishers.find((p) => p.name.toLowerCase() === pubNames[0]?.trim().toLowerCase())) autoCreated.push("publisher");
        if (resolvedCategoryId && !categories.find((c) => c.name.toLowerCase() === catNames[0]?.trim().toLowerCase())) autoCreated.push("category");
      }

      const autoMsg = autoCreated.length > 0 ? ` (auto-created: ${autoCreated.join(", ")})` : "";
      toast.success(`Found: ${data.title} (${data.source})${autoMsg}`);
    } catch {
      toast.error("Book not found for this ISBN");
    } finally {
      setIsbnLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyBookForm());
    setExistingCopyAccessions([]);
    setPreviewAccessions([]);
    setAccessionInput("");
    setTakenAccessions([]);
    setSelectedAuthorIds([]);
    setIsbnQuery("");
    setDialogOpen(true);
  };

  const openEdit = async (book: Book) => {
    setEditing(book);
    setAccessionInput("");
    setTakenAccessions([]);
    const authorNames = book.authors || [];
    const matchedIds = authorNames
      .map((name) =>
        authorsList.find((a) => a.name.trim().toLowerCase() === String(name || "").trim().toLowerCase())?.id,
      )
      .filter(Boolean) as string[];
    setSelectedAuthorIds(matchedIds);
    setForm({
      title: book.title,
      isbn: book.isbn,
      authors: authorNames.join(", "),
      category_id: book.category_id || "",
      publisher_id: book.publisher_id || "",
      publication_year: String(book.publication_year || ""),
      edition: book.edition || "",
      pages: String(book.pages || ""),
      language: book.language || "English",
      cover_image_url: book.cover_image_url || "",
      description: book.description || "",
      copies: "0",
    });
    try {
      const res = await api.get(`/books/${book.id}/copies`);
      const accessions = (res.data || []).map((copy: BookCopy) => String(copy.accession_no)).filter(Boolean);
      setExistingCopyAccessions(accessions);
    } catch {
      setExistingCopyAccessions([]);
    }
    setDialogOpen(true);
  };

  const createBookCopies = async (bookId: string, accessionNumbers: string[]) => {
    await api.post("/books/copies/batch", {
      book_id: parseInt(bookId, 10),
      count: accessionNumbers.length,
      accession_numbers: accessionNumbers,
    });
  };

  const handleSave = async () => {
    const errors: string[] = [];
    if (!form.title.trim()) errors.push("Title");
    if (!form.category_id) errors.push("Category");
    if (!form.publisher_id) errors.push("Publisher");
    if (selectedAuthorIds.length === 0) errors.push("Authors");
    if (errors.length > 0) {
      toast.error(`Required fields missing: ${errors.join(", ")}`);
      return;
    }

    const copyCount = parseInt(form.copies, 10) || 0;
    if (!editing) {
      if (copyCount < 1) {
        toast.error("At least one copy is required");
        return;
      }
    }

    if (copyCount > 0) {
      if (accessionError) {
        toast.error(accessionError);
        return;
      }
      try {
        const taken = await checkAccessionsTaken(parsedAccessions);
        if (taken.length) {
          setTakenAccessions(taken);
          toast.error(`Already in register: ${taken.join(", ")}`);
          return;
        }
      } catch {
        toast.error("Could not verify accession numbers");
        return;
      }
    }

    const authorNames = selectedAuthorIds.map((id) => authorsList.find((a) => a.id === id)?.name).filter(Boolean) as string[];

    const { copies, ...bookFields } = form;
    const payload = {
      ...bookFields,
      authors: authorNames,
      publication_year: parseInt(form.publication_year) || 0,
      pages: parseInt(form.pages) || 0,
    };
    try {
      if (editing) {
        await api.put(`/books/${editing.id}`, payload);
        if (copyCount > 0) {
          await createBookCopies(editing.id, parsedAccessions);
        }
        toast.success(copyCount > 0 ? `Book updated and ${copyCount} copy/copies added` : "Book updated");
      } else {
        const bookRes = await api.post("/books", payload);
        await createBookCopies(String(bookRes.data.id), parsedAccessions);
        toast.success(`Book created with ${copyCount} copy/copies`);
      }
      setDialogOpen(false);
      fetchBooks();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (book: Book) => {
    const label = book.title || book.isbn || "this book";
    if (!window.confirm(`Delete book "${label}"?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/books/${book.id}`);
      toast.success("Book deleted");
      setDialogOpen(false);
      setEditing(null);
      fetchBooks();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/excel/export/books", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "books.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const quickAdd = async () => {
    if (!quickAddName.trim()) return;
    const typeMap: Record<string, string> = { author: "authors", category: "categories", publisher: "publishers" };
    try {
      const res = await api.post(`/masters/${typeMap[quickAddType]}`, { name: quickAddName.trim() });
      toast.success(`${quickAddType} added`);
      setQuickAddType("");
      setQuickAddName("");
      await loadMasters();
      if (quickAddType === "category") setForm((f) => ({ ...f, category_id: String(res.data.id) }));
      if (quickAddType === "publisher") setForm((f) => ({ ...f, publisher_id: String(res.data.id) }));
      if (quickAddType === "author") setSelectedAuthorIds((prev) => [...prev, String(res.data.id)]);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to add");
    }
  };

  const toggleAuthor = (id: string) => {
    setSelectedAuthorIds((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };

  const categoryOptions: ColumnFilterOption[] = categories.map((c) => ({ label: c.name, value: String(c.id) }));
  const publisherOptions: ColumnFilterOption[] = publishers.map((p) => ({ label: p.name, value: String(p.id) }));

  const resolvePublisher = (book: Book) => publishers.find((p) => String(p.id) === String(book.publisher_id))?.name || "";

  const columns: any[] = [
    {
      accessorKey: "cover_image_url",
      header: "Cover",
      meta: { columnClassName: "w-[52px]" },
      cell: ({ row }: any) =>
        row.original.cover_image_url ? (
          <img
            src={`${api.defaults.baseURL}/books/${row.original.id}/cover`}
            alt=""
            className="w-10 h-14 object-cover rounded"
            onError={(e: any) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          <div className="w-10 h-14 bg-gray-100 rounded flex items-center justify-center text-[10px] text-gray-400">No img</div>
        ),
    },
    {
      accessorKey: "title",
      header: "Title",
      meta: {
        filterVariant: "text",
        filterPlaceholder: "Title...",
        columnClassName: "min-w-[180px]",
      },
      cell: ({ row }: any) => {
        const authors = (row.original.authors || []).join(", ");
        const publisher = resolvePublisher(row.original);
        return (
          <div className="min-w-0 space-y-0.5">
            <span className="font-medium leading-snug block">{row.original.title}</span>
            {row.original.isbn && <p className="text-sm text-muted-foreground font-mono">{row.original.isbn}</p>}
            {authors && <p className="text-sm text-muted-foreground">{authors}</p>}
            {publisher && <p className="text-sm text-muted-foreground">{publisher}</p>}
          </div>
        );
      },
    },
    {
      accessorKey: "isbn",
      header: "ISBN",
      meta: { filterVariant: "text", filterPlaceholder: "ISBN...", columnClassName: "hidden" },
    },
    {
      accessorKey: "authors",
      header: "Authors",
      meta: { filterVariant: "text", filterPlaceholder: "Author...", columnClassName: "hidden" },
    },
    {
      accessorKey: "publisher_id",
      header: "Publisher",
      meta: { filterVariant: "select", filterOptions: publisherOptions, filterPlaceholder: "Publisher", columnClassName: "hidden" },
    },
    {
      accessorKey: "category_id",
      header: "Category",
      meta: { filterVariant: "select", filterOptions: categoryOptions, filterPlaceholder: "Category", columnClassName: "hidden" },
    },
    {
      id: "copies",
      accessorKey: "total_copies",
      header: "Copies",
      meta: { columnClassName: "w-[88px] whitespace-nowrap" },
      cell: ({ row }: any) => {
        const total = row.original.total_copies || 0;
        const available = row.original.available_copies || 0;
        return (
          <span className={`font-medium tabular-nums ${available > 0 ? "text-green-700" : "text-red-600"}`}>
            {available} / {total}
          </span>
        );
      },
    },
  ];

  return (
    <div className="space-y-6" data-testid="accession-register-page">
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight whitespace-nowrap">
          Accession Register
        </h1>
        {isAdmin && (
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center gap-2 w-full">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={handleExport} data-testid="export-books-btn">
                    <Download className="w-4 h-4" />
                    <span className="sr-only">Export Excel</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Export Excel</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    onClick={() => setImportOpen(true)}
                    data-testid="import-books-btn"
                  >
                    <Upload className="w-4 h-4" />
                    <span className="sr-only">Import Excel</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Import Excel</TooltipContent>
              </Tooltip>
              <Button className="flex-1 min-w-0 h-10 text-sm bg-primary hover:bg-primary/90" onClick={openNew} data-testid="add-book-btn">
                <Plus className="w-4 h-4 mr-1.5 shrink-0" />
                Add Book
              </Button>
            </div>
          </TooltipProvider>
        )}
      </div>

      <DataTable
        columns={columns}
        data={books}
        totalPages={totalPages}
        currentPage={page}
        onPageChange={setPage}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        loading={loading}
        onRowClick={isAdmin ? openEdit : undefined}
        data-testid="books-table"
      />

      <ExcelImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        entityType="books"
        entityLabel="Books"
        templateDescription="Download the Library Register workbook already used by Bindley. Each row represents one accession entry, and ISBN values will be used to auto-fill missing book metadata when available."
        uploadDescription="Upload the filled Library Register sheet here. The importer creates or reuses the book record, then creates one copy per accession number."
        onSuccess={() => {
          fetchBooks();
          loadMasters();
        }}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl w-[calc(100%-1.5rem)] max-h-[90dvh] flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>{editing ? "Edit Book" : "Add New Book"}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden px-6 pb-4">
          {!editing && (
            <div className="flex gap-2 p-3 bg-muted rounded-md mb-4 min-w-0">
              <Input
                placeholder="Enter ISBN to auto-fill"
                value={isbnQuery}
                onChange={(e) => setIsbnQuery(e.target.value)}
                className="bg-card min-w-0"
                onKeyDown={(e) => e.key === "Enter" && lookupISBN()}
              />
              <Button onClick={lookupISBN} disabled={isbnLoading} className="bg-[var(--beat)] hover:bg-[var(--beat)]/90 shrink-0">
                <SearchIcon className="w-4 h-4 mr-1" /> {isbnLoading ? "..." : "Lookup"}
              </Button>
            </div>
          )}
          {isAdmin && (
            <div className="flex justify-end mb-1">
              <Button type="button" variant="outline" size="sm" onClick={enrichWithAi} disabled={aiEnrichLoading}>
                {aiEnrichLoading ? "Enriching..." : "AI enrich catalog"}
              </Button>
            </div>
          )}
          <div className="flex flex-col md:flex-row gap-4 md:gap-6 min-w-0">
            <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-1.5 min-w-0">
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="book-title-input" className="min-w-0" />
              </div>
              <div className="space-y-1.5 min-w-0">
                <Label>ISBN</Label>
                <div className="flex items-center gap-2 min-w-0">
                  <Input value={form.isbn} onChange={(e) => setForm({ ...form, isbn: e.target.value })} className="font-mono min-w-0" />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={openCameraScanner}
                    title="Scan ISBN with camera"
                    aria-label="Scan ISBN with camera"
                  >
                    <Camera className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5 min-w-0">
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <Label className="shrink-0">Authors *</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs px-1.5 text-primary shrink-0"
                    onClick={() => {
                      setQuickAddType("author");
                      setQuickAddName("");
                    }}
                  >
                    <PlusCircle className="w-3 h-3 mr-1" /> Add New
                  </Button>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full min-w-0 max-w-full justify-between font-normal h-auto min-h-9 py-1.5">
                      {selectedAuthorIds.length > 0 ? (
                        <span className="truncate text-sm">
                          {selectedAuthorIds.length} author{selectedAuthorIds.length > 1 ? "s" : ""} selected
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">Select authors...</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search authors..." />
                      <CommandList>
                        <CommandEmpty>No authors found.</CommandEmpty>
                        <CommandGroup>
                          {authorsList.map((a) => (
                            <CommandItem key={a.id} value={a.name} onSelect={() => toggleAuthor(a.id)} className="cursor-pointer">
                              <Check className={`mr-2 h-4 w-4 ${selectedAuthorIds.includes(a.id) ? "opacity-100" : "opacity-0"}`} />
                              {a.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {selectedAuthorIds.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedAuthorIds.map((id) => {
                      const a = authorsList.find((x) => x.id === id);
                      return a ? (
                        <Badge key={id} variant="secondary" className="text-xs">
                          {a.name}{" "}
                          <button onClick={() => toggleAuthor(id)} className="ml-1">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
              <div className="space-y-1.5 min-w-0">
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <Label className="shrink-0">Category *</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs px-1.5 text-primary shrink-0"
                    onClick={() => {
                      setQuickAddType("category");
                      setQuickAddName("");
                    }}
                  >
                    <PlusCircle className="w-3 h-3 mr-1" /> Add New
                  </Button>
                </div>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 min-w-0">
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <Label className="shrink-0">Publisher *</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs px-1.5 text-primary shrink-0"
                    onClick={() => {
                      setQuickAddType("publisher");
                      setQuickAddName("");
                    }}
                  >
                    <PlusCircle className="w-3 h-3 mr-1" /> Add New
                  </Button>
                </div>
                <Select value={form.publisher_id} onValueChange={(v) => setForm({ ...form, publisher_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select publisher" />
                  </SelectTrigger>
                  <SelectContent>
                    {publishers.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Year</Label>
                <Input type="number" value={form.publication_year} onChange={(e) => setForm({ ...form, publication_year: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Edition</Label>
                <Input value={form.edition} onChange={(e) => setForm({ ...form, edition: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Pages</Label>
                <Input type="number" value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Language</Label>
                <Input value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} />
              </div>
              <div className="sm:col-span-2 rounded-md border bg-muted/50 p-3 space-y-3 min-w-0 overflow-hidden">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {editing ? "Add Copies" : "Accession Copies"}
                </p>
                {editing && (
                  <div className="text-sm text-muted-foreground space-y-1 min-w-0">
                    <p>
                      Existing copies: <span className="font-medium text-foreground">{existingCopyAccessions.length || editing.total_copies || 0}</span>
                    </p>
                    {existingCopyAccessions.length > 0 && (
                      <p className="font-mono text-xs break-all">Acc. nos: {existingCopyAccessions.join(", ")}</p>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
                  <div className="space-y-1.5 min-w-0">
                    <Label>{editing ? "Copies to Add" : "Number of Copies *"}</Label>
                    <Input
                      type="number"
                      min={editing ? 0 : 1}
                      value={form.copies}
                      onChange={(e) => setForm({ ...form, copies: e.target.value })}
                      data-testid="book-copies-input"
                      className="min-w-0"
                    />
                  </div>
                  {((editing && (parseInt(form.copies, 10) || 0) > 0) || !editing) && (
                    <div className="space-y-1.5 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <Label>Accession Numbers</Label>
                        {previewAccessions.length > 0 && !previewLoading && accessionInput.trim() !== previewAccessions.join(", ") && (
                          <button
                            type="button"
                            className="text-xs text-muted-foreground underline hover:text-foreground"
                            onClick={() => setAccessionInput(previewAccessions.join(", "))}
                          >
                            Use suggested
                          </button>
                        )}
                      </div>
                      <Input
                        value={accessionInput}
                        placeholder={
                          previewLoading ? "Calculating..." : previewAccessions.length > 0 ? `Suggested: ${previewAccessions.join(", ")}` : "e.g. 9021, 9022"
                        }
                        onChange={(e) => setAccessionInput(e.target.value)}
                        aria-invalid={!!accessionError && parsedAccessions.length > 0}
                        data-testid="book-accession-input"
                        className={`min-w-0 font-mono ${accessionError && parsedAccessions.length > 0 ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                      />
                    </div>
                  )}
                </div>
                {expectedAccessionCount > 0 && (
                  <p className={`text-xs break-all ${accessionError && parsedAccessions.length > 0 ? "text-red-600" : "text-muted-foreground"}`}>
                    {parsedAccessions.length > 0 && accessionError
                      ? accessionError
                      : parsedAccessions.length > 0 && checkingAccessions
                        ? "Checking availability..."
                        : parsedAccessions.length > 0
                          ? "All accession numbers are available."
                          : `Enter ${expectedAccessionCount} accession number(s), separated by commas. Each must be unique across the register.`}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2 space-y-1.5 min-w-0">
                <Label>Cover Image URL</Label>
                <Input
                  value={form.cover_image_url}
                  onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
                  className="min-w-0 max-w-full"
                />
                {form.cover_image_url && (
                  <div className="md:hidden pt-1 max-w-full overflow-hidden">
                    <img
                      src={form.cover_image_url}
                      alt="Cover"
                      className="h-36 w-auto max-w-full rounded-md shadow-md border object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="sm:col-span-2 space-y-1.5 min-w-0">
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="min-w-0 max-w-full"
                />
              </div>
            </div>
            {form.cover_image_url && (
              <div className="hidden md:block w-40 shrink-0 space-y-2">
                <Label className="text-xs text-muted-foreground">Cover Preview</Label>
                <img
                  src={form.cover_image_url}
                  alt="Cover"
                  className="w-full max-h-64 rounded-md shadow-md border object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            )}
          </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t bg-background shrink-0 !flex-row !flex-nowrap justify-end gap-2 space-x-0">
            {editing && (
              <Button
                type="button"
                className="flex-1 min-w-0 h-11 text-base font-semibold bg-red-600 text-white hover:bg-red-700"
                onClick={() => void handleDelete(editing)}
                data-testid="delete-book-btn"
              >
                <Trash2 className="w-5 h-5 mr-1.5 shrink-0" /> Delete
              </Button>
            )}
            <Button
              className="flex-1 min-w-0 h-11 text-base font-semibold bg-slate-200 text-slate-800 hover:bg-slate-300"
              onClick={() => setDialogOpen(false)}
            >
              <X className="w-5 h-5 mr-1.5 shrink-0" /> Cancel
            </Button>
            <Button
              className="flex-1 min-w-0 h-11 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={handleSave}
              data-testid="save-book-btn"
            >
              <Check className="w-5 h-5 mr-1.5 shrink-0" /> {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={scannerOpen}
        onOpenChange={(open) => {
          setScannerOpen(open);
          if (!open) stopCamera();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Scan ISBN</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <video ref={cameraVideoRef} className="w-full rounded-md border bg-black" playsInline muted autoPlay />
            <p className="text-xs text-muted-foreground">Align the barcode in view, then capture to detect ISBN.</p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setScannerOpen(false);
                stopCamera();
              }}
            >
              Cancel
            </Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={captureAndScanISBN} disabled={scannerLoading}>
              {scannerLoading ? "Scanning..." : "Capture & Scan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!quickAddType} onOpenChange={(o) => !o && setQuickAddType("")}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              Add New {quickAddType?.charAt(0).toUpperCase()}
              {quickAddType?.slice(1)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input
                value={quickAddName}
                onChange={(e) => setQuickAddName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && quickAdd()}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickAddType("")}>
              Cancel
            </Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={quickAdd}>
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
